import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { MembershipAPI } from '@saraa/permissions/api/MembershipAPI.js';
import { type OrganizationMembership, SaraaOrgRole } from '@saraa/permissions/db/SaraaUser.js';
import { html, nothing, type TemplateResult } from 'lit';
import { OrganizationSelect } from './OrganizationSelect.js';
import { emptyState, sectionLoader } from './ui.js';

const ROLE_LABELS: Record<SaraaOrgRole, string> = {
  [SaraaOrgRole.ORG_ADMIN]: 'Organization admin',
  [SaraaOrgRole.ORG_USER]: 'Organization user',
};

const ROLE_DESCRIPTIONS: Record<SaraaOrgRole, string> = {
  [SaraaOrgRole.ORG_ADMIN]: 'Manages this organization: its templates, template fields and members.',
  [SaraaOrgRole.ORG_USER]: 'Uses the generate page with this organization’s templates.',
};

/**
 * Where a user belongs, and as what.
 *
 * This is the single source of truth for per-organization access — the ABAC role
 * assignment beside it only says which *kinds* of permission a user can exercise;
 * these decide **where** each one applies.
 *
 * Deliberately detached from the surrounding user form. Memberships are not in
 * that form's selection set and are never part of the entity the ERM autosaves,
 * because the generic update path rewrites the whole array by index: a save that
 * is a perfect no-op against the state it was checked on would still re-write
 * every slot, restoring a membership revoked in the meantime. Each change here is
 * instead a single organization-keyed mutation touching only the organization it
 * names, and saves immediately rather than with the rest of the form.
 */
export const OrganizationMembershipsSection = stateful(({ userId }: { userId: string }) => {
  const [reloadToken, setReloadToken] = useState(0);
  const [draftOrganizationId, setDraftOrganizationId] = useState<string | undefined>(undefined);
  const [draftRole, setDraftRole] = useState<SaraaOrgRole>(SaraaOrgRole.ORG_USER);

  const membershipsQuery = useQuery(
    () => MembershipAPI.read(userId)({ _id: 1, organizationId: 1, role: 1 }),
    [userId, reloadToken],
  );
  const memberships = (membershipsQuery.value ?? []) as OrganizationMembership[];

  const reload = () => setReloadToken(reloadToken + 1);

  const setMembership = (organizationId: string, role: SaraaOrgRole, loadingText: string) =>
    guardDialog(async () => await MembershipAPI.set(userId, organizationId, role)(), {
      loadingText,
      success: () => reload(),
    });

  const removeMembership = (organizationId: string) =>
    guardDialog(async () => await MembershipAPI.remove(userId, organizationId)(), {
      hasToConfirm: true,
      confirmText: 'Remove this user from the organization?',
      loadingText: 'Removing…',
      success: () => reload(),
    });

  const addDraft = async () => {
    if (!draftOrganizationId) return;
    await setMembership(draftOrganizationId, draftRole, 'Adding…');
    setDraftOrganizationId(undefined);
    setDraftRole(SaraaOrgRole.ORG_USER);
  };

  /**
   * The role's description sits under the whole row rather than inside the role column.
   *
   * It used to be the last child of that column, which made the column taller than the one
   * beside it — and with the row bottom-aligned, the two field labels ended up on different
   * lines, roughly 50px apart. Lifting the description out leaves two columns of equal
   * height, so labels, fields and the remove button all line up on their own.
   */
  const membershipRow = (membership: OrganizationMembership): TemplateResult => html`
    <div class="rounded-box border-base-300 flex flex-col gap-2 border p-4">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div class="flex-1">
          <p class="text-base-content/70 mb-1 text-xs font-medium">Organization</p>
          ${OrganizationSelect({ value: membership.organizationId ?? undefined, disabled: true })}
        </div>
        <div class="flex-1">
          <p class="text-base-content/70 mb-1 text-xs font-medium">Role</p>
          ${DropdownSelectionInput<SaraaOrgRole>({
            selectables: Object.values(SaraaOrgRole),
            renderString: role => ROLE_LABELS[role] ?? role,
            value: membership.role ?? undefined,
            onPicked: role => {
              if (role && membership.organizationId) void setMembership(membership.organizationId, role, 'Saving…');
            },
          })}
        </div>
        <button
          type="button"
          class="btn btn-ghost btn-sm text-error shrink-0"
          aria-label="Remove from organization"
          @click=${() => membership.organizationId && void removeMembership(membership.organizationId)}
        >
          <span class="icon-[material-symbols--delete-outline] text-lg"></span>
        </button>
      </div>
      ${membership.role
        ? html`<p class="text-base-content/50 text-xs">${ROLE_DESCRIPTIONS[membership.role]}</p>`
        : nothing}
    </div>
  `;

  return html`
    <section class="flex flex-col gap-4">
      <!-- No "Organizations" heading here: the ERM section this renders into is already titled that. -->
      <p class="text-base-content/70 text-sm">
        Which organizations this user belongs to, and their role in each. Changes here save immediately, one
        organization at a time. A user with no membership can sign in but sees nothing.
      </p>

      ${membershipsQuery.loading
        ? sectionLoader({ compact: true })
        : memberships.length === 0
          ? emptyState('No organizations yet.', { compact: true })
          : html`<div class="flex flex-col gap-3">${memberships.map(membershipRow)}</div>`}

      <div class="rounded-box border-base-300 flex flex-col gap-3 border border-dashed p-4 sm:flex-row sm:items-end">
        <div class="flex-1">
          <p class="text-base-content/70 mb-1 text-xs font-medium">Add to organization</p>
          ${OrganizationSelect({
            value: draftOrganizationId,
            onPicked: organizationId => setDraftOrganizationId(organizationId ?? undefined),
          })}
        </div>
        <div class="flex-1">
          <p class="text-base-content/70 mb-1 text-xs font-medium">Role</p>
          ${DropdownSelectionInput<SaraaOrgRole>({
            selectables: [SaraaOrgRole.ORG_USER, SaraaOrgRole.ORG_ADMIN],
            renderString: role => ROLE_LABELS[role] ?? role,
            value: draftRole,
            onPicked: role => setDraftRole(role ?? SaraaOrgRole.ORG_USER),
          })}
        </div>
        <button type="button" class="btn btn-sm gap-1.5" ?disabled=${!draftOrganizationId} @click=${addDraft}>
          <span class="icon-[material-symbols--add] text-base"></span> Add
        </button>
      </div>
    </section>
  `;
});
